import { Injectable } from '@angular/core';
import { AccountGroup } from '@galaxy/account-group';
import { InboxNavigationService } from '@galaxy/conversation/core';
import { BrandingV2Interface, BrandingV2Service } from '@galaxy/partner';
import { ConversationChannel, GlobalParticipantType, SubjectParticipant } from '@vendasta/conversation';
import { TaskInterface } from '@vendasta/task';
import { Observable, of } from 'rxjs';
import { catchError, map, shareReplay, take } from 'rxjs/operators';
import { OrganizationInfoInterface, OrganizationType, adapterOrganizationType } from '../shared/interfaces';

@Injectable()
export class SendMessageDialogService {
  constructor(private brandingService: BrandingV2Service, private inboxNav: InboxNavigationService) {}

  redirectToConversation(
    partnerId: string,
    recipientOrganizationId: string,
    organizationType: OrganizationType,
    prefilledMessage?: string,
  ): void {
    this.inboxNav.gotoConversationChannel(
      [
        new SubjectParticipant({
          internalParticipantId: partnerId,
          participantType: GlobalParticipantType.GLOBAL_PARTICIPANT_TYPE_PARTNER,
        }),
        new SubjectParticipant({
          internalParticipantId: recipientOrganizationId,
          participantType: adapterOrganizationType(organizationType),
        }),
      ],
      ConversationChannel.CONVERSATION_CHANNEL_INTERNAL,
      prefilledMessage,
    );
  }

  buildProjectOrganizations(
    project: TaskInterface,
    currentPartnerId: string,
    accountGroupInfo: AccountGroup,
  ): OrganizationInfoInterface[] {
    const accountGroupOrg = [
      {
        id: accountGroupInfo?.accountGroupId,
        name: of(accountGroupInfo?.napData?.companyName),
        type: OrganizationType.ACCOUNT_GROUP,
        address: accountGroupInfo?.napData,
      } as OrganizationInfoInterface,
    ];
    const partnerOrgs = this.getPartnerVendorInfo(
      project,
      currentPartnerId,
      OrganizationType.PARTNER,
      OrganizationType.PARTNER,
    );
    const vendorOrgs = this.getPartnerVendorInfo(
      project,
      currentPartnerId,
      'FulfillingPartnerId',
      OrganizationType.VENDOR,
    );
    return [...accountGroupOrg, ...partnerOrgs, ...vendorOrgs];
  }

  private getPartnerVendorInfo(
    project: TaskInterface,
    currentPartnerId: string,
    keyValueType: string,
    organizationType: OrganizationType,
  ): OrganizationInfoInterface[] {
    const organizations: OrganizationInfoInterface[] = [];
    project?.metadata?.keyValues?.forEach((data) => {
      if (data?.key === keyValueType && data?.values[0]?.stringValue !== currentPartnerId) {
        const organizationInfo = {
          id: data?.values[0]?.stringValue,
          type: organizationType,
          name: this.getPartnerName(data?.values[0]?.stringValue),
        } as OrganizationInfoInterface;
        organizations.push(organizationInfo);
      }
    });
    return organizations;
  }

  private getPartnerName(partnerId: string): Observable<string> {
    return this.brandingService.getBranding(partnerId).pipe(
      take(1),
      shareReplay(1),
      catchError(() => {
        return of({} as BrandingV2Interface);
      }),
      map((branding) => branding?.name ?? ''),
    );
  }
}
